import { Info, Plus, Trash2, GripVertical } from "lucide-react";
import {
    EVENT_REASONS_MAX_LENGTH,
    LR_ROW_ID_KEY,
    lrShortcodeTriggerTestId,
} from "../configTabHelpers";
import companyFormStyles from "@/features/company/styles/companyAdd.module.css";
import { ConfigSubsection } from "./ConfigSubsection";
import type { LrEventConfigTableProps } from "../../../types";

export function LrEventConfigTable(props: LrEventConfigTableProps) {
    const {
        addLrRow,
        lrEvents,
        setLrEvents,
        availableLrEvents,
        availableLrActions,
        lrDuplicateRowIndexes,
        lrSaveAttempted,
        setLrDragOverIndex,
        lrDraggingIndex,
        lrDragOverIndex,
        handleLrRowDrop,
        handleLrRowDragStart,
        handleLrRowDragEnd,
        getActionCommentRequired,
        lrCommentInteracted,
        setLrCommentInteracted,
        setLrShortcodeTooltip,
        setVisibleLrDenyGuestTooltips,
        visibleLrDenyGuestTooltips,
        handleLrShortcodeInfoClick,
        closeLrDenyGuestTooltip,
        removeLrRow,
    } = props;
    return (
        <ConfigSubsection
            title="LR Event Config"
            testId="company-config-lr-event-config-section"
            headerActions={
                <button
                    type="button"
                    data-testid="company-config-lr-event-add-row-button"
                    onClick={addLrRow}
                    className={`${companyFormStyles.btnPrimary} gap-2 py-1.5 text-[13px]`}
                >
                    <Plus className="h-4 w-4" /> Add New Event
                </button>
            }
        >
    <div className="w-full min-w-0 overflow-x-auto">
        <table className="w-full min-w-[780px] table-fixed border-collapse">
            <colgroup>
                <col className="w-[22%]" />
                <col className="w-[22%]" />
                <col className="w-[44%]" />
                <col className="w-[12%]" />
            </colgroup>
            <thead>
                <tr className="bg-[#fcfcfc] border-b border-[#eaeaea]">
                    <th className="px-3 py-3 text-left text-[13px] font-semibold uppercase tracking-tight text-slate-600 sm:px-4">
                        Look For
                    </th>
                    <th className="px-3 py-3 text-left text-[13px] font-semibold uppercase tracking-tight text-slate-600 sm:px-4">
                        Take Action
                    </th>
                    <th className="min-w-0 px-3 py-3 text-left text-[13px] font-semibold uppercase tracking-tight text-slate-600 sm:px-4">
                        Comment
                    </th>
                    <th className="px-2 py-3 text-left text-[13px] font-semibold uppercase tracking-tight text-slate-600 whitespace-nowrap sm:px-3">
                        Action
                    </th>
                </tr>
            </thead>
            <tbody>
                {lrEvents.map((row, idx) => (
                    <tr
                        key={String(row[LR_ROW_ID_KEY] ?? `lr-${idx}`)}
                        data-testid={`company-config-lr-event-row-${idx}`}
                        onDragEnter={(e) => {
                            e.preventDefault();
                        }}
                        onDragOverCapture={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            setLrDragOverIndex((prev) => (prev === idx ? prev : idx));
                        }}
                        onDrop={(e) => handleLrRowDrop(e, idx)}
                        className={`border-b border-[#f0f0f0] transition-colors ${
                            lrDraggingIndex === idx ? "opacity-60" : ""
                        } ${
                            lrDragOverIndex === idx && lrDraggingIndex !== idx
                                ? "bg-sky-50/90 ring-1 ring-inset ring-[#1597ff]/35"
                                : "hover:bg-slate-50/50"
                        }`}
                    >
                        <td className="px-3 py-3 align-top sm:px-4">
                            <select
                                data-testid={`company-config-lr-event-row-${idx}-look-for-select`}
                                value={row.event_type}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setLrEvents((prev) => {
                                        const news = [...prev];
                                        news[idx] = {
                                            ...news[idx],
                                            event_type: v,
                                            lr_action: "",
                                            event_reasons: "",
                                        };
                                        return news;
                                    });
                                    setLrShortcodeTooltip(null);
                                    setVisibleLrDenyGuestTooltips((prev) => {
                                        const next = [...prev];
                                        if (idx < next.length) next[idx] = false;
                                        return next;
                                    });
                                }}
                                className={`h-10 w-full min-w-0 rounded border bg-white px-2.5 py-2 text-[13px] outline-none focus:outline-none ${
                                    lrDuplicateRowIndexes.has(idx)
                                        ? "border-red-500 focus:border-red-500"
                                        : lrSaveAttempted && !String(row.event_type ?? "").trim()
                                          ? "border-red-500 focus:border-red-500"
                                          : "border-[#eaeaea] focus:border-[#1597ff]"
                                }`}
                            >
                                <option value="">Select Event</option>
                                {availableLrEvents.map((e) => (
                                    <option key={e.event_type} value={e.event_type}>{e.event_label}</option>
                                ))}
                            </select>
                            {lrDuplicateRowIndexes.has(idx) && (
                                <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                    <span
                                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                        aria-hidden
                                    >
                                        !
                                    </span>
                                    <span>Event/Action combination is duplicate.</span>
                                </div>
                            )}
                            {lrSaveAttempted && !String(row.event_type ?? "").trim() && (
                                <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                    <span
                                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                        aria-hidden
                                    >
                                        !
                                    </span>
                                    <span>Event is required.</span>
                                </div>
                            )}
                        </td>
                        <td className="px-3 py-3 align-top sm:px-4">
                            <select
                                data-testid={`company-config-lr-event-row-${idx}-take-action-select`}
                                value={row.lr_action}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setLrEvents((prev) => {
                                        const news = [...prev];
                                        news[idx] = {
                                            ...news[idx],
                                            lr_action: v,
                                            event_reasons: "",
                                        };
                                        return news;
                                    });
                                    setLrShortcodeTooltip(null);
                                    const commentReq = getActionCommentRequired(v, availableLrActions);
                                    setVisibleLrDenyGuestTooltips((prev) => {
                                        const next = [...prev];
                                        while (next.length <= idx) next.push(false);
                                        next[idx] = v === "denyGuestCard" && commentReq;
                                        return next;
                                    });
                                    // Show required validation as soon as a comment-required action is selected (Angular marks touched on change).
                                    setLrCommentInteracted((prev) => {
                                        const next = [...prev];
                                        while (next.length <= idx) next.push(false);
                                        next[idx] = commentReq;
                                        return next;
                                    });
                                }}
                                className={`h-10 w-full min-w-0 rounded border bg-white px-2.5 py-2 text-[13px] outline-none focus:outline-none ${
                                    lrDuplicateRowIndexes.has(idx)
                                        ? "border-red-500 focus:border-red-500"
                                        : lrSaveAttempted && !String(row.lr_action ?? "").trim()
                                          ? "border-red-500 focus:border-red-500"
                                          : "border-[#eaeaea] focus:border-[#1597ff]"
                                }`}
                            >
                                <option value="">Select Action</option>
                                {availableLrActions
                                    .filter((a) =>
                                        a.action_type !== "uploadPolicyDocumentGuestCard" ||
                                        row.event_type === "ContractPurchased" ||
                                        row.event_type === "ContractUpdated" ||
                                        row.event_type === "ContractFeePartialPayment"
                                    )
                                    .map((a) => (
                                        <option key={a.action_type} value={a.action_type}>{a.action_label}</option>
                                    ))}
                            </select>
                            {lrDuplicateRowIndexes.has(idx) && (
                                <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                    <span
                                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                        aria-hidden
                                    >
                                        !
                                    </span>
                                    <span>Event/Action combination is duplicate.</span>
                                </div>
                            )}
                            {lrSaveAttempted && !String(row.lr_action ?? "").trim() && (
                                <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                    <span
                                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                        aria-hidden
                                    >
                                        !
                                    </span>
                                    <span>Action is required.</span>
                                </div>
                            )}
                        </td>
                        <td className="min-w-0 overflow-visible px-3 py-3 align-top sm:px-4">
                            {(() => {
                                const lrCommentRequired = getActionCommentRequired(
                                    row.lr_action,
                                    availableLrActions,
                                );
                                const lrCommentEmpty = !String(row.event_reasons ?? "").trim();
                                const lrCommentLen = String(row.event_reasons ?? "").length;
                                const lrOverMax = lrCommentLen > EVENT_REASONS_MAX_LENGTH;
                                const lrAtMax =
                                    lrCommentRequired &&
                                    lrCommentLen === EVENT_REASONS_MAX_LENGTH;
                                const lrShowRequiredError =
                                    lrCommentRequired &&
                                    lrCommentEmpty &&
                                    (lrCommentInteracted[idx] || lrSaveAttempted);
                                return (
                                    <div className="min-w-0">
                                        <div className="relative min-w-0">
                                            <textarea
                                                data-testid={`company-config-lr-event-row-${idx}-comment-textarea`}
                                                value={row.event_reasons}
                                                onChange={(e) => {
                                                    const news = [...lrEvents];
                                                    news[idx].event_reasons = e.target.value;
                                                    setLrEvents(news);
                                                    setLrCommentInteracted((prev) => {
                                                        const next = [...prev];
                                                        while (next.length <= idx) next.push(false);
                                                        next[idx] = true;
                                                        return next;
                                                    });
                                                }}
                                                onBlur={() =>
                                                    setLrCommentInteracted((prev) => {
                                                        const next = [...prev];
                                                        while (next.length <= idx) next.push(false);
                                                        next[idx] = true;
                                                        return next;
                                                    })
                                                }
                                                rows={1}
                                                disabled={!lrCommentRequired}
                                                className={`min-h-[44px] w-full resize-y rounded border bg-white px-3 py-2.5 text-[13px] leading-normal text-slate-800 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400 ${
                                                    lrShowRequiredError || lrOverMax
                                                        ? "border-red-500 focus:border-red-500"
                                                        : "border-[#eaeaea] focus:border-[#1597ff]"
                                                }`}
                                                placeholder="Please enter action comment"
                                                maxLength={EVENT_REASONS_MAX_LENGTH}
                                            />
                                            {lrCommentRequired && row.lr_action !== "denyGuestCard" && (
                                                <button
                                                    type="button"
                                                    data-testid={lrShortcodeTriggerTestId(idx)}
                                                    data-lr-shortcode-trigger
                                                    className="absolute top-[10px] -right-[22px] cursor-pointer border-0 bg-transparent p-0 text-[16px] leading-none text-[green] transition-opacity hover:opacity-80 focus:outline-none focus-visible:rounded-full focus-visible:ring-2 focus-visible:ring-green-600/50 focus-visible:ring-offset-1"
                                                    title="Shortcodes"
                                                    aria-label="Show shortcodes for this event"
                                                    onClick={(e) => handleLrShortcodeInfoClick(idx, e)}
                                                >
                                                    <Info className="h-4 w-4" strokeWidth={2.25} aria-hidden />
                                                </button>
                                            )}
                                            {row.lr_action === "denyGuestCard" &&
                                                visibleLrDenyGuestTooltips[idx] && (
                                                    <div className="event_reasons_info_lr relative z-[150] mt-2 w-full max-w-[min(100%,28rem)] rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-left text-[13px] leading-snug text-amber-900 shadow-lg">
                                                        <button
                                                            type="button"
                                                            data-testid={`company-config-lr-event-row-${idx}-deny-guest-tooltip-close-button`}
                                                            className="absolute right-2 top-2 border-0 bg-transparent p-0 text-base leading-none text-amber-800 hover:text-amber-950"
                                                            onClick={() => closeLrDenyGuestTooltip(idx)}
                                                            aria-label="Close"
                                                        >
                                                            ×
                                                        </button>
                                                        <div className="pr-7">
                                                            Please provide a valid reason that matches an
                                                            entry on your Yardi portal. Failure to do so may
                                                            result in this action being rejected.
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                        {lrShowRequiredError && (
                                            <div className="mt-1.5 flex items-start gap-2 text-[13px] font-normal text-red-600">
                                                <span
                                                    className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                                    aria-hidden
                                                >
                                                    !
                                                </span>
                                                <span>Action comment is required.</span>
                                            </div>
                                        )}
                                        {lrAtMax && (
                                            <p className="mt-1.5 text-[12px] leading-snug text-slate-500">
                                                Maximum {EVENT_REASONS_MAX_LENGTH} characters.
                                            </p>
                                        )}
                                        {lrOverMax && (
                                            <div className="mt-1.5 flex items-start gap-2 text-[13px] font-normal text-red-600">
                                                <span
                                                    className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                                    aria-hidden
                                                >
                                                    !
                                                </span>
                                                <span>Action comment must not exceed 240 characters.</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </td>
                        <td className="px-2 py-3 align-top sm:px-3">
                            <div className="flex items-center justify-center gap-1">
                                <button
                                    type="button"
                                    data-testid={`company-config-lr-event-row-${idx}-remove-row-button`}
                                    onClick={() => removeLrRow(idx)}
                                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded border border-[#e5e5e5] bg-white p-0 text-slate-400 transition-colors hover:border-slate-300 hover:text-red-500"
                                    title="Remove row"
                                    aria-label="Remove row"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                                <span
                                    data-testid={`company-config-lr-event-row-${idx}-drag-handle`}
                                    draggable
                                    onDragStart={(e) => {
                                        e.stopPropagation();
                                        handleLrRowDragStart(e, idx);
                                    }}
                                    onDragEnd={handleLrRowDragEnd}
                                    className="inline-flex h-9 w-9 shrink-0 cursor-grab select-none items-center justify-center rounded border border-[#e5e5e5] bg-white text-slate-400 touch-none hover:border-slate-300 hover:text-slate-600 active:cursor-grabbing"
                                    title="Drag to reorder"
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Drag to reorder row"
                                >
                                    <GripVertical className="pointer-events-none h-4 w-4" />
                                </span>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
        </ConfigSubsection>
    );
}
